/**
 * GA4 chat metrics (property in `frontend/public/index.html`).
 *
 * Events:
 * - `chat_session_start` — new thread / standalone video (`session_unit`)
 * - `chat_session_end` — visit over (`duration_sec` = text + video combined)
 * - `chat_surface_end` — time on one surface (`duration_sec`, `surface` text|video)
 * - `message_sent` — user turn (`message_unit`, `surface`)
 *
 * GA4 Admin for avg time in text vs video:
 * - Custom metric on `duration_sec` (Event scope, Standard, **Average**)
 * - Dimension **Surface** on `surface`
 * - Detail report: Event name = `chat_surface_end`, dimension Surface, metric Avg duration_sec
 */



declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

export type ChatAnalyticsSurface = 'text' | 'video';

const MESSAGE_UNIT = 1;
const SESSION_UNIT = 1;

type GaParams = Record<string, string | number | boolean | undefined>;

type VisitEntry = {
  visitStartedAt: number;
  surfaceStartedAt: number;
  surface: ChatAnalyticsSurface;
};

const activeVisits = new Map<string, VisitEntry>();

function toGaParams(params: GaParams): Record<string, string | number | boolean> {
  return Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== ''),
  ) as Record<string, string | number | boolean>;
}

function emitEvent(eventName: string, params?: GaParams): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('event', eventName, params ? toGaParams(params) : {});
}

function surfaceDurationSec(entry: VisitEntry): number {
  return Math.max(0, Math.round((Date.now() - entry.surfaceStartedAt) / 1000));

}

function visitDurationSec(entry: VisitEntry): number {
  return Math.max(0, Math.round((Date.now() - entry.visitStartedAt) / 1000));
}

function flushSurfaceSegment(chatSessionId: string, entry: VisitEntry): void {
  const durationSec = surfaceDurationSec(entry);
  emitEvent('chat_surface_end', {
    chat_session_id: chatSessionId,
    surface: entry.surface,
    duration_sec: durationSec,
  });

  entry.surfaceStartedAt = Date.now();
}

function startVisit(chatSessionId: string, surface: ChatAnalyticsSurface): void {
  const now = Date.now();
  activeVisits.set(chatSessionId, {
    visitStartedAt: now,
    surfaceStartedAt: now,
    surface,
  });
}

/** Move wall clock to another surface within the same thread visit. */

export function switchChatSurface(
  chatSessionId: string,
  surface: ChatAnalyticsSurface,
): void {

  const entry = activeVisits.get(chatSessionId);

  if (entry == null) {
    startVisit(chatSessionId, surface);
    return;
  }

  if (entry.surface === surface) return;
  flushSurfaceSegment(chatSessionId, entry);
  entry.surface = surface;
}

/** New thread / standalone video call + visit timer. */

export function trackChatSessionStart(
  chatSessionId: string,
  surface: ChatAnalyticsSurface = 'text',
): void {
  startVisit(chatSessionId, surface);
  emitEvent('chat_session_start', {
    chat_session_id: chatSessionId,
    surface,
    session_unit: SESSION_UNIT,
  });
}

/** Reopen an existing thread: visit timer only (no `session_unit`). */
export function beginChatWallClock(
  chatSessionId: string,
  surface: ChatAnalyticsSurface = 'text',
): void {
  startVisit(chatSessionId, surface);
}

/** End visit: flush active surface segment, then total visit duration. */
export function trackChatSessionEnd(chatSessionId: string | null | undefined): void {
  if (!chatSessionId) return;
  const entry = activeVisits.get(chatSessionId);
  activeVisits.delete(chatSessionId);
  if (entry == null) return;

  flushSurfaceSegment(chatSessionId, entry);
  emitEvent('chat_session_end', {
    chat_session_id: chatSessionId,
    duration_sec: visitDurationSec(entry),
  });
}

/** One user-authored turn (text send or new video transcript question). */
export function trackMessageSent(chatSessionId: string, surface: ChatAnalyticsSurface): void {
  emitEvent('message_sent', {
    chat_session_id: chatSessionId,
    surface,
    message_unit: MESSAGE_UNIT,
  });
}
